import * as React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';

import axios from '../utils/axios';
import { normalizeAdminNotificationSettings } from '../utils/notification-settings';

const DEFAULT_SYSTEM_NAME = 'Liga!';
const DEFAULT_LOGO_PATH = '/logos/logo_300x300.png';
const DEFAULT_LOGO_COMPACT_PATH = '/logos/logo_icone.png';

const defaultSettings = {
  systemName: DEFAULT_SYSTEM_NAME,
  browserTitle: '',
  slogan: '',
  appPublicUrl: '',
  logoUrl: DEFAULT_LOGO_PATH,
  logoCompactUrl: DEFAULT_LOGO_COMPACT_PATH,
  faviconUrl: null,
  googleClientId: null,
  googleAuthEnabled: false,
  notificationSettings: normalizeAdminNotificationSettings(),
  appSettings: {
    androidDownloadUrl: '',
    postagemFixadaTimelineId: null,
    exibirCardDownloadApp: true,
    exibirPostagemFixadaTimeline: true,
  },
  feedPostMediaMaxHeightPx: 360,
  feedPostTextMaxHeightPx: 220,
  feedAutoplayVideos: true,
};

const BrandingContext = React.createContext({
  settings: defaultSettings,
  isLoading: false,
  isError: false,
  setSettings: () => {},
});

const BrandingProvider = ({ children }) => {
  const [settings, setSettings] = React.useState(defaultSettings);

  const brandingQuery = useQuery(
    'system-settings',
    async () => {
      const response = await axios.get('/api/system-settings');
      return response.data.settings;
    },
    {
      retry: (failureCount, error) => {
        if (error?.response?.status === 429 || error?.response?.status === 404)
          return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 30000),
      staleTime: 5 * 60 * 1000,
      onSuccess: (nextSettings) => {
        setSettings((currentSettings) => ({
          ...currentSettings,
          ...nextSettings,
        }));
      },
    }
  );

  React.useEffect(() => {
    const faviconHref = settings.faviconUrl || '/favicon.ico';
    const cacheKey = settings.updatedAt || Date.now();
    const finalHref = faviconHref.includes('?')
      ? `${faviconHref}&v=${encodeURIComponent(cacheKey)}`
      : `${faviconHref}?v=${encodeURIComponent(cacheKey)}`;

    const resolveType = (href) => {
      const normalizedHref = String(href || '').toLowerCase();
      if (normalizedHref.includes('.png')) return 'image/png';
      if (normalizedHref.includes('.svg')) return 'image/svg+xml';
      if (normalizedHref.includes('.webp')) return 'image/webp';
      if (normalizedHref.includes('.ico')) return 'image/x-icon';
      return 'image/x-icon';
    };

    ['icon', 'shortcut icon', 'apple-touch-icon'].forEach((relValue) => {
      const selector = `link[rel='${relValue}']`;
      let faviconLink = document.querySelector(selector);

      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.setAttribute('rel', relValue);
        document.head.appendChild(faviconLink);
      }

      faviconLink.setAttribute('href', finalHref);
      faviconLink.setAttribute('type', resolveType(faviconHref));
    });
  }, [settings.faviconUrl, settings.updatedAt]);

  const value = React.useMemo(
    () => ({
      settings,
      isLoading: brandingQuery.isLoading,
      isError: brandingQuery.isError,
      setSettings,
    }),
    [brandingQuery.isError, brandingQuery.isLoading, settings]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

const useBranding = () => {
  const context = React.useContext(BrandingContext);

  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }

  return context;
};

BrandingProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

export { BrandingProvider, useBranding, defaultSettings };
